export interface TeacherImportContext {
  availableClasses: {
    id: string;
    name: string;
  }[];
}

export type TeacherRowStatus = 'valid' | 'error' | 'duplicate';

export interface ParsedTeacherRow {
  rowNumber: number;
  email: string;
  full_name: string;
  classes: string;
  classNames: string[];
  resolvedClassIds: string[];
  unknownClassNames: string[];
  warnings: string[];
  errors: string[];
  status: TeacherRowStatus;
  isDuplicateInCsv: boolean;
  imported?: boolean;
  outcome?: string;
  importError?: string;
}

interface RawTeacherCsvRow {
  rowNumber: number;
  email: string;
  full_name: string;
  classes: string;
}

export interface ParseTeacherCsvResult {
  rows: RawTeacherCsvRow[];
  parseError?: string;
}

const REQUIRED_COLUMNS = ['email', 'full_name', 'classes'] as const;

function splitCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }

  result.push(current.trim());
  return result;
}

export function parseTeacherCsv(text: string): ParseTeacherCsvResult {
  const lines = text.split(/\r?\n/);

  let headerLineIndex = 0;
  while (headerLineIndex < lines.length && lines[headerLineIndex].trim() === '') {
    headerLineIndex++;
  }
  if (headerLineIndex >= lines.length) {
    return { rows: [], parseError: 'File is empty.' };
  }

  const headers = splitCsvLine(lines[headerLineIndex]).map((h) =>
    h.toLowerCase().replace(/\s+/g, '_'),
  );

  const missing = REQUIRED_COLUMNS.filter((c) => !headers.includes(c));
  if (missing.length > 0) {
    return {
      rows: [],
      parseError: `Missing required columns: ${missing.join(', ')}. Found: ${headers.join(', ')}`,
    };
  }

  const get = (values: string[], col: string): string => {
    const idx = headers.indexOf(col);
    return idx >= 0 ? (values[idx] ?? '') : '';
  };

  const rows: RawTeacherCsvRow[] = [];
  let rowNumber = 0;

  for (let i = headerLineIndex + 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') continue;

    const values = splitCsvLine(line);
    if (values.every((v) => v === '')) continue;

    rowNumber++;
    rows.push({
      rowNumber,
      email: get(values, 'email'),
      full_name: get(values, 'full_name'),
      classes: get(values, 'classes'),
    });
  }

  return { rows };
}

function isValidEmail(email: string): boolean {
  return /^[^@]+@[^@]+\.[^@]+$/.test(email);
}

export function validateTeacherCsvRows(
  raw: RawTeacherCsvRow[],
  context: TeacherImportContext,
): ParsedTeacherRow[] {
  const classIdByName = new Map<string, string>();
  for (const cls of context.availableClasses) {
    classIdByName.set(cls.name.trim().toLowerCase(), cls.id);
  }

  const emailCounts = new Map<string, number>();
  for (const row of raw) {
    const key = row.email.trim().toLowerCase();
    emailCounts.set(key, (emailCounts.get(key) ?? 0) + 1);
  }

  return raw.map((row) => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const trimmedEmail = row.email.trim().toLowerCase();
    const classNames = row.classes
      .split(',')
      .map((name) => name.trim())
      .filter(Boolean);

    if (!trimmedEmail) {
      errors.push('email is required');
    } else if (!isValidEmail(trimmedEmail)) {
      errors.push(`email "${row.email}" is invalid`);
    }

    const resolvedClassIds: string[] = [];
    const unknownClassNames: string[] = [];

    for (const className of classNames) {
      const classId = classIdByName.get(className.toLowerCase());
      if (classId) {
        resolvedClassIds.push(classId);
      } else {
        unknownClassNames.push(className);
      }
    }

    if (unknownClassNames.length > 0) {
      warnings.push(
        `Unknown classes will be ignored: ${unknownClassNames.join(', ')}`,
      );
    }

    const isDuplicateInCsv = !!trimmedEmail && (emailCounts.get(trimmedEmail) ?? 0) > 1;
    let status: TeacherRowStatus = 'valid';

    if (errors.length > 0) {
      status = 'error';
    } else if (isDuplicateInCsv) {
      status = 'duplicate';
    }

    return {
      rowNumber: row.rowNumber,
      email: trimmedEmail,
      full_name: row.full_name.trim(),
      classes: row.classes,
      classNames,
      resolvedClassIds: [...new Set(resolvedClassIds)],
      unknownClassNames,
      warnings,
      errors,
      status,
      isDuplicateInCsv,
    };
  });
}

export interface TeacherImportSummary {
  total: number;
  valid: number;
  error: number;
  duplicate: number;
}

export function summariseTeacherRows(rows: ParsedTeacherRow[]): TeacherImportSummary {
  return {
    total: rows.length,
    valid: rows.filter((row) => row.status === 'valid').length,
    error: rows.filter((row) => row.status === 'error').length,
    duplicate: rows.filter((row) => row.status === 'duplicate').length,
  };
}
